import { useState } from "react";
import { Panel, Empty, fmt, shortDate } from "./kit";
import { AutoTable } from "./AutoData";
import { Trash2 } from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { web } from "../../lib/api";
import { autoBenchmark, benchmarkLabel, rankOf } from "../../lib/benchmarks";

export const METRICS = [
  ["height_no_shoes_in", "Height (no shoes)", "in"], ["height_with_shoes_in", "Height (shoes)", "in"],
  ["weight_lb", "Weight", "lb"], ["wingspan_in", "Wingspan", "in"], ["standing_reach_in", "Standing reach", "in"],
  ["body_fat_pct", "Body fat", "%"], ["standing_vert_in", "Standing vert", "in"], ["running_vert_in", "Running vert", "in"],
  ["bench_reps", "Bench reps", ""], ["lane_agility_s", "Lane agility", "s"], ["three_quarter_sprint_s", "3/4 sprint", "s"],
  ["reactive_shuttle_s", "Reactive shuttle", "s"],
];

const SessionTable = ({ sessions, onDelete }) => {
  const cols = METRICS.filter(([k]) => sessions.some((s) => s[k] != null));
  return (
    <div className="overflow-x-auto">
      <table data-testid="combine-sessions-table" className="w-full min-w-[520px] text-left text-sm">
        <thead><tr className="font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">
          <th className="py-2 pr-3">Date</th>{cols.map(([k, l]) => <th key={k} className="px-2 py-2">{l}</th>)}<th />
        </tr></thead>
        <tbody>
          {sessions.map((s, i) => (
            <tr key={s.id ?? i} data-testid={`combine-session-${s.id ?? i}`} className="border-t border-[#1E293B] font-mono-gd text-white">
              <td className="py-2 pr-3 font-sans text-slate-200">{shortDate(s.date)}</td>
              {cols.map(([k, , u]) => <td key={k} className="px-2 py-2">{s[k] == null ? "—" : `${fmt(s[k])}${u}`}</td>)}
              <td className="py-2 text-right">{s.id && <button data-testid={`combine-delete-${s.id}`} onClick={() => onDelete(s)} aria-label="Delete session" className="rounded-full p-2 text-slate-400 hover:text-red-200"><Trash2 className="h-4 w-4" /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const BenchmarkNote = ({ comparison }) => (
  <div data-testid="benchmark-note" className="mt-4 rounded-xl border border-[#1E293B] bg-[#0F172A] p-4 text-sm text-slate-300">
    <p className="font-semibold text-white">What this benchmark is</p>
    <p className="mt-1">
      The average measurements of {comparison?.label ? `${comparison.label.toLowerCase()}s` : "players at this position"} who were later drafted into the NBA,
      taken at the NBA Draft Combine{(() => { const y = comparison?.sample_label?.match(/\d{4}\s*[–-]\s*\d{4}/)?.[0]; return y ? ` (${y} sample)` : ""; })()}. These are pre-draft pro prospects, mostly college-age,
      so most high school players will measure below them. Use it as a long-term target, not a grade.
    </p>
  </div>
);

const BenchmarkPicker = ({ value, options, onChange }) => (
  <label className="no-print flex items-center gap-2 font-mono-gd text-xs uppercase tracking-wider text-slate-400">
    Compare to
    <select data-testid="benchmark-select" value={value} onChange={(e) => onChange(e.target.value)}
      className="rounded-lg border border-[#1E293B] bg-[#0F172A] px-3 py-1.5 font-sans text-sm normal-case tracking-normal text-white outline-none focus:border-[#39FF14]">
      {options.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
    </select>
  </label>
);

const SourceNote = ({ b }) => (
  <div data-testid="benchmark-source" className="mt-4 rounded-xl border border-[#1E293B] bg-[#0F172A] p-4 text-sm text-slate-300">
    <p className="font-semibold text-white">{benchmarkLabel(b)} benchmark</p>
    <p className="mt-1">Source: {b.source_name} ({b.source_year})</p>
    {b.notes && <p className="mt-1 text-slate-400">{b.notes}</p>}
  </div>
);

export const RankChip = ({ r, testid }) => (r ? (
  <span data-testid={testid} className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 font-mono-gd text-[11px] font-bold uppercase tracking-wider ${r.score >= 75 ? "bg-[#39FF14] text-[#0A0E1A]" : r.score >= 50 ? "bg-[#39FF14]/15 text-[#39FF14]" : "bg-[#162032] text-slate-300"}`}>{r.label}</span>
) : <span className="text-slate-500">—</span>);

export function CombinePanel({ latest, bests, comparison, sessions, onDelete, emptyText, player }) {
  const list = useApi("/benchmarks", web);
  const tables = Array.isArray(list.data) ? list.data : [];
  const [picked, setPicked] = useState(null);
  const auto = autoBenchmark(tables, player);
  const selId = picked ?? auto?.id ?? (comparison ? "nba" : tables[0]?.id ?? "nba");
  const custom = tables.find((b) => b.id === selId);
  const cmp = custom ? custom.metrics ?? {} : comparison?.metrics ?? {};
  const options = [...(comparison ? [["nba", "NBA Draft Combine average"]] : []), ...tables.map((b) => [b.id, benchmarkLabel(b)])];
  const colLabel = custom ? `${benchmarkLabel(custom)} avg` : "NBA draftee avg";
  const rows = METRICS.filter(([k]) => latest?.[k] != null || bests?.[k] != null);
  const ranked = custom && Object.keys(custom.percentiles ?? {}).length > 0;
  if (!rows.length && !sessions?.length) return <Empty title="No combine sessions" text={emptyText ?? "Combine testing logged in the mobile app shows up here."} />;
  return (
    <div className="space-y-6" data-testid="combine-panel">
      <Panel>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
          <h3 className="font-display text-xl font-bold uppercase text-white">Combine vs. {custom ? benchmarkLabel(custom) : "NBA Draft Combine average"}</h3>
          {options.length > 1 && <BenchmarkPicker value={selId} options={options} onChange={setPicked} />}
          {!custom && comparison?.label && <p data-testid="benchmark-label" className="w-full font-mono-gd text-xs uppercase tracking-wider text-slate-400">Drafted {comparison.label}s · {comparison.sample_label}</p>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead><tr className="font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">
              <th className="py-2">Metric</th><th className="py-2">Latest</th><th className="py-2">Best</th><th className="py-2" data-testid="benchmark-column">{colLabel}</th>{ranked && <th className="py-2">Rank (best)</th>}
            </tr></thead>
            <tbody>
              {rows.map(([k, label, unit]) => (
                <tr key={k} data-testid={`combine-row-${k}`} className="border-t border-[#1E293B] font-mono-gd">
                  <td className="py-2 font-sans text-slate-200">{label}</td>
                  <td className="py-2 text-[#39FF14]">{fmt(latest?.[k])}{latest?.[k] != null && unit}</td>
                  <td className="py-2 text-white">{fmt(bests?.[k])}{bests?.[k] != null && unit}</td>
                  <td className="py-2 text-slate-400">{fmt(cmp[k])}{cmp[k] != null && unit}</td>
                  {ranked && <td className="py-2"><RankChip r={rankOf(k, bests?.[k] ?? latest?.[k], custom.percentiles?.[k])} testid={`combine-rank-${k}`} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {custom ? <SourceNote b={custom} /> : comparison && <BenchmarkNote comparison={comparison} />}
        {!custom && Array.isArray(comparison?.sources) && (
          <p className="mt-4 text-xs text-slate-400">
            Sources: {comparison.sources.map((s, i) => (
              <a key={s.url ?? i} href={s.url} target="_blank" rel="noreferrer" className="mr-3 underline decoration-[#39FF14]/50 hover:text-white">{s.label}</a>
            ))}
          </p>
        )}
      </Panel>
      {sessions?.length > 0 && (
        <Panel>
          <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">All sessions</h3>
          {onDelete ? <SessionTable sessions={sessions} onDelete={onDelete} /> : <AutoTable rows={sessions} testid="combine-sessions-table" />}
        </Panel>
      )}
    </div>
  );
}
